import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import moment from "moment";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import PeriodSelect from "./PeriodSelect";
import WidgetSkeleton from "./WidgetSkeleton";
import { ExdBackButton, ExdInfoTip } from "./ExdControls";
import { EXD, ExdCrumb, ExdHint, widgetSx } from "./exdMui";

const TAB_OPTIONS = [
  { key: "all", label: "All" },
  { key: "services", label: "Services" },
  { key: "parts", label: "Parts" },
  { key: "packages", label: "Packages" },
];

const MIX_COLORS = {
  services: "#2f5bea",
  parts: "#f08c1a",
  packages: "#16a36a",
};

const BAR_COLORS = MIX_COLORS;

const MONTH_PARSE_FORMATS = [
  "YYYY-MM",
  "YYYY/MM",
  "YYYYMM",
  "MMM YYYY",
  "MMMM YYYY",
  "MMM-YYYY",
  "MMM-YY",
  "MM-YYYY",
  "M-YYYY",
];

const parsePeriodMoment = (value) => {
  if (value == null || value === "") return null;
  const str = String(value).trim();
  // Day-of-month numbers (daily drill) — keep as-is
  if (/^\d{1,2}$/.test(str)) return null;
  for (const format of MONTH_PARSE_FORMATS) {
    const parsed = moment(str, format, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
};

const formatMonthLabel = (value) => {
  const parsed = parsePeriodMoment(value);
  if (parsed) return parsed.format("MMM YYYY");
  return String(value ?? "");
};

const formatMonthRange = (first, last) => {
  const start = parsePeriodMoment(first);
  const end = parsePeriodMoment(last);
  if (start && end) {
    if (start.isSame(end, "month")) return start.format("MMM YYYY");
    return `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}`;
  }
  const a = formatMonthLabel(first);
  const b = formatMonthLabel(last);
  if (!a) return "";
  return a === b ? a : `${a} – ${b}`;
};

const RoundedBar = (props) => {
  const { x, y, width, height, fill } = props;
  if (height < 0) return null;
  return (
    <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />
  );
};

const SalesChartTooltip = ({
  active,
  payload,
  label,
  activeTab,
  loginUserCurrency,
}) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce(
    (sum, entry) => sum + (Number(entry.value) || 0),
    0,
  );

  return (
    <Box
      sx={{
        bgcolor: EXD.text,
        borderRadius: "8px",
        boxShadow: "0 8px 24px rgba(22,26,58,0.28)",
        color: "#fff",
        minWidth: 150,
        p: "8px 10px",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
        {formatMonthLabel(label)}
      </Typography>
      {payload.map((entry) => (
        <Stack
          key={entry.dataKey}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ fontSize: 12, mb: 0.375 }}
        >
          <Box
            sx={{
              bgcolor: entry.color,
              borderRadius: "3px",
              height: 8,
              width: 8,
            }}
          />
          <Typography sx={{ color: "#c9cde3", fontSize: 12 }}>
            {entry.name?.charAt(0).toUpperCase() + entry.name?.slice(1)}
          </Typography>
          <Typography component="strong" sx={{ fontSize: 12, ml: "auto" }}>
            {nFormatter(entry.value, 2)}{" "}
            <CurrencySymbol
              currency={loginUserCurrency}
              withParentheses={false}
            />
          </Typography>
        </Stack>
      ))}
      {activeTab === "all" && (
        <Typography
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            fontSize: 12,
            fontWeight: 700,
            mt: 0.75,
            pt: 0.75,
          }}
        >
          Total: {nFormatter(total, 2)}{" "}
          <CurrencySymbol
            currency={loginUserCurrency}
            withParentheses={false}
          />
        </Typography>
      )}
    </Box>
  );
};

const SalesMixPanel = ({
  totals,
  grandTotal,
  loginUserCurrency,
  crumb,
  topMonth,
}) => {
  const rows = [
    { key: "services", label: "Services", value: totals.services },
    { key: "parts", label: "Parts", value: totals.parts },
    { key: "packages", label: "Packages", value: totals.packages },
  ];

  return (
    <Paper
      elevation={0}
      component="section"
      sx={{
        ...widgetSx("chart"),
        height: "100%",
        justifyContent: "stretch",
      }}
    >
      <Typography
        component="h3"
        noWrap
        sx={{
          color: EXD.text,
          fontSize: 13.5,
          fontWeight: 700,
          m: 0,
        }}
      >
        Sales Mix
        {crumb ? (
          <>
            {" "}
            <ExdCrumb>{crumb}</ExdCrumb>
          </>
        ) : null}
      </Typography>

      {/* Stacked share strip */}
      <Box
        sx={{
          bgcolor: EXD.surface2,
          borderRadius: "10px",
          display: "flex",
          height: 14,
          mt: 1.5,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {rows.map((row) => {
          const pct = grandTotal ? (row.value / grandTotal) * 100 : 0;
          if (pct <= 0) return null;
          return (
            <Box
              key={row.key}
              title={`${row.label}: ${pct.toFixed(1)}%`}
              sx={{
                bgcolor: MIX_COLORS[row.key],
                flex: `0 0 ${pct}%`,
                height: "100%",
              }}
            />
          );
        })}
      </Box>

      <Stack
        spacing={0}
        sx={{
          flex: 1,
          justifyContent: "space-evenly",
          minHeight: 220,
          mt: 1,
          width: "100%",
        }}
      >
        {rows.map((row) => {
          const pct = grandTotal ? (row.value / grandTotal) * 100 : 0;
          return (
            <Box key={row.key} sx={{ width: "100%" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 0.75 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.875}>
                  <Box
                    sx={{
                      bgcolor: MIX_COLORS[row.key],
                      borderRadius: "4px",
                      height: 10,
                      width: 10,
                    }}
                  />
                  <Typography
                    sx={{ color: EXD.text, fontSize: 13, fontWeight: 700 }}
                  >
                    {row.label}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    color: EXD.text,
                    fontFamily: EXD.font,
                    fontSize: 18,
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 700,
                  }}
                >
                  {pct.toFixed(0)}%
                </Typography>
              </Stack>
              <Box
                sx={{
                  bgcolor: EXD.surface2,
                  borderRadius: 99,
                  height: 10,
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    bgcolor: MIX_COLORS[row.key],
                    borderRadius: 99,
                    height: "100%",
                    minWidth: pct > 0 ? 6 : 0,
                    width: `${Math.min(Math.max(pct, 0), 100)}%`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: EXD.muted,
                  fontFamily: EXD.font,
                  fontSize: 12,
                  fontWeight: 600,
                  mt: 0.5,
                }}
              >
                {nFormatter(row.value, 2)}{" "}
                <CurrencySymbol
                  currency={loginUserCurrency}
                  withParentheses={false}
                />
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {topMonth?.name ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{
            borderTop: `1px dashed ${EXD.border}`,
            mt: 1,
            pt: 1.25,
          }}
        >
          <Typography sx={{ color: EXD.muted, fontSize: 12, fontWeight: 600 }}>
            Top month:{" "}
            <Box component="span" sx={{ color: EXD.text, fontWeight: 700 }}>
              {formatMonthLabel(topMonth.name)}
            </Box>
          </Typography>
          <Typography
            component="b"
            sx={{
              color: EXD.text,
              fontFamily: EXD.font,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {nFormatter(topMonth.total, 2)}{" "}
            <CurrencySymbol
              currency={loginUserCurrency}
              withParentheses={false}
            />
          </Typography>
        </Stack>
      ) : null}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{
          borderTop: `1px dashed ${EXD.border}`,
          mt: topMonth?.name ? 1 : "auto",
          pt: 1.25,
        }}
      >
        <ExdCrumb>Total in range</ExdCrumb>
        <Typography
          component="b"
          sx={{ fontFamily: EXD.font, fontSize: 15, fontWeight: 700 }}
        >
          {nFormatter(grandTotal, 2)}{" "}
          <CurrencySymbol
            currency={loginUserCurrency}
            withParentheses={false}
          />
        </Typography>
      </Stack>
    </Paper>
  );
};

const SalesChartWidget = ({
  loading,
  nestedLoading,
  translate,
  cogsMonthly,
  date,
  tab2,
  onPeriodChange,
  onBack,
  chartData,
  handleBarClickWrapper,
  loginUserCurrency,
  infoTitle,
}) => {
  const [activeTab, setActiveTab] = useState("all");

  const transformedData = useMemo(() => {
    if (!chartData?.xAxis?.categories || !chartData?.series) return [];

    const categories = chartData.xAxis.categories;
    const servicesData =
      chartData.series.find((s) => s.name?.toLowerCase() === "services")
        ?.data || [];
    const partsData =
      chartData.series.find((s) => s.name?.toLowerCase() === "parts")?.data ||
      [];
    const packagesData =
      chartData.series.find((s) => s.name?.toLowerCase() === "packages")
        ?.data || [];

    return categories.map((month, i) => ({
      month: month || date,
      services: servicesData[i] ?? 0,
      parts: partsData[i] ?? 0,
      packages: packagesData[i] ?? 0,
    }));
  }, [chartData, date]);

  const totals = useMemo(() => {
    return transformedData.reduce(
      (acc, row) => {
        acc.services += Number(row.services) || 0;
        acc.parts += Number(row.parts) || 0;
        acc.packages += Number(row.packages) || 0;
        return acc;
      },
      { services: 0, parts: 0, packages: 0 },
    );
  }, [transformedData]);

  const grandTotal = totals.services + totals.parts + totals.packages;

  const topMonth = useMemo(() => {
    if (!transformedData.length) return null;
    let best = null;
    transformedData.forEach((row) => {
      const total =
        (Number(row.services) || 0) +
        (Number(row.parts) || 0) +
        (Number(row.packages) || 0);
      if (!best || total > best.total) {
        best = { name: row.month, total };
      }
    });
    return best?.total > 0 ? best : null;
  }, [transformedData]);

  const mixCrumb = useMemo(() => {
    if (!transformedData.length) return "";
    if (!cogsMonthly) return date || "";
    const first = transformedData[0]?.month;
    const last = transformedData[transformedData.length - 1]?.month;
    if (!first) return "";
    return formatMonthRange(first, last);
  }, [transformedData, cogsMonthly, date]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "grid",
          gap: "10px",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 2.2fr) minmax(0, 1fr)",
          },
          width: "100%",
        }}
      >
        <WidgetSkeleton />
        <WidgetSkeleton />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: "stretch",
        display: "grid",
        gap: "10px",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "minmax(0, 2.2fr) minmax(0, 1fr)",
        },
        width: "100%",
      }}
    >
      <Paper elevation={0} component="section" sx={{ ...widgetSx("chart"), height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          spacing={1}
          useFlexGap
          sx={{ minHeight: 28 }}
        >
          <Typography
            component="h3"
            noWrap
            sx={{
              color: EXD.text,
              flex: "1 1 160px",
              fontSize: 13.5,
              fontWeight: 700,
              m: 0,
              minWidth: 0,
            }}
          >
            {translate("Sales Chart")}
            <ExdCrumb>
              {" "}
              {cogsMonthly
                ? "· Services · Parts · Packages"
                : `· ${translate("Month")} : ${date || ""}`}
            </ExdCrumb>
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={activeTab}
            onChange={(_, next) => {
              if (next != null) setActiveTab(next);
            }}
            aria-label="sales chart tabs"
            sx={{
              bgcolor: EXD.surface2,
              border: `1px solid ${EXD.border}`,
              borderRadius: "9px",
              gap: "2px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: 0,
                borderRadius: "7px !important",
                color: EXD.muted,
                fontSize: 12.5,
                fontWeight: 600,
                m: 0,
                px: 1.375,
                py: 0.625,
                textTransform: "none",
                "&.Mui-selected": {
                  bgcolor: EXD.surface,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                  color: EXD.text,
                },
              },
            }}
          >
            {TAB_OPTIONS.map((tab) => (
              <ToggleButton key={tab.key} value={tab.key}>
                {tab.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ ml: "auto" }}
          >
            {cogsMonthly ? (
              <PeriodSelect
                value={tab2}
                onChange={onPeriodChange}
                fallbackLabel="Last Year"
              />
            ) : (
              <ExdBackButton onClick={onBack}>
                {translate("back")}
              </ExdBackButton>
            )}
            <ExdInfoTip title={infoTitle} />
          </Stack>
        </Stack>

        <Box sx={{ minHeight: 260, overflow: "visible", position: "relative" }}>
          {nestedLoading ? (
            <WidgetSkeleton />
          ) : (
            <Box sx={{ minHeight: 260, overflow: "visible", position: "relative", zIndex: 2 }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={transformedData}
                  barCategoryGap={14}
                  margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={EXD.border}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={{ stroke: EXD.border }}
                    tickLine={false}
                    tick={{ fill: EXD.muted, fontSize: 11, fontWeight: 600 }}
                    tickFormatter={(value) => formatMonthLabel(value)}
                  />
                  <YAxis
                    tickFormatter={(value) => nFormatter(value, 1)}
                    tick={{ fill: EXD.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(47, 91, 234, 0.06)" }}
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={{
                      outline: "none",
                      pointerEvents: "none",
                      zIndex: 10050,
                    }}
                    content={
                      <SalesChartTooltip
                        activeTab={activeTab}
                        loginUserCurrency={loginUserCurrency}
                      />
                    }
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    height={28}
                    iconType="circle"
                    formatter={(value) =>
                      value.charAt(0).toUpperCase() + value.slice(1)
                    }
                    wrapperStyle={{ fontSize: 12, fontWeight: 600 }}
                  />

                  {activeTab === "all" ? (
                    <>
                      <Bar
                        dataKey="services"
                        name="services"
                        stackId="a"
                        fill={BAR_COLORS.services}
                        shape={<RoundedBar />}
                        onClick={handleBarClickWrapper}
                        style={{ cursor: cogsMonthly ? "pointer" : "default" }}
                      />
                      <Bar
                        dataKey="parts"
                        name="parts"
                        stackId="a"
                        fill={BAR_COLORS.parts}
                        shape={<RoundedBar />}
                        onClick={handleBarClickWrapper}
                        style={{ cursor: cogsMonthly ? "pointer" : "default" }}
                      />
                      <Bar
                        dataKey="packages"
                        name="packages"
                        stackId="a"
                        fill={BAR_COLORS.packages}
                        shape={<RoundedBar />}
                        onClick={handleBarClickWrapper}
                        style={{ cursor: cogsMonthly ? "pointer" : "default" }}
                      />
                    </>
                  ) : (
                    <Bar
                      dataKey={activeTab}
                      name={activeTab}
                      fill={BAR_COLORS[activeTab]}
                      shape={<RoundedBar />}
                      onClick={handleBarClickWrapper}
                      style={{ cursor: cogsMonthly ? "pointer" : "default" }}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              {cogsMonthly ? (
                <ExdHint>Click a month bar to drill into daily sales</ExdHint>
              ) : null}
            </Box>
          )}
        </Box>
      </Paper>

      <SalesMixPanel
        totals={totals}
        grandTotal={grandTotal}
        loginUserCurrency={loginUserCurrency}
        crumb={mixCrumb}
        topMonth={topMonth}
      />
    </Box>
  );
};

export default SalesChartWidget;
